<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">匯入紀錄</h1>
      <button class="btn btn-primary" @click="handleNewImport">
        <i class="ri-upload-line"></i>
        新增匯入
      </button>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="loadJobs">
          <option value="">所有狀態</option>
          <option value="pending">待處理</option>
          <option value="processing">處理中</option>
          <option value="completed">已完成</option>
          <option value="partial">部分成功</option>
          <option value="failed">失敗</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="loadJobs">
        <i class="ri-refresh-line"></i>
        重新整理
      </button>
    </div>

    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="jobs.length === 0" class="empty-state">
        <i class="ri-file-upload-line empty-icon"></i>
        <p>尚無匯入紀錄</p>
        <button class="btn btn-primary" @click="handleNewImport">開始匯入</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>作業編號</th>
              <th>檔案名稱</th>
              <th>格式</th>
              <th>總筆數</th>
              <th>成功</th>
              <th>失敗</th>
              <th>跳過</th>
              <th>狀態</th>
              <th>匯入時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in jobs" :key="job.id">
              <td class="job-number">{{ job.jobNumber }}</td>
              <td class="file-name" :title="job.fileName">{{ job.fileName }}</td>
              <td>
                <span class="format-badge" :class="`format-${job.fileFormat}`">
                  {{ formatLabel(job.fileFormat) }}
                </span>
              </td>
              <td>{{ formatNumber(job.totalRows) }}</td>
              <td class="count-success">{{ formatNumber(job.successCount) }}</td>
              <td class="count-error">{{ formatNumber(job.failedCount) }}</td>
              <td class="count-skip">{{ formatNumber(job.skippedCount) }}</td>
              <td>
                <span class="status-badge" :class="`status-${job.status}`">
                  {{ statusLabel(job.status) }}
                </span>
                <span v-if="job.revokedAt" class="status-badge status-revoked">已撤銷</span>
              </td>
              <td>{{ formatDate(job.createdAt) }}</td>
              <td>
                <div class="action-group">
                  <button
                    v-if="(job.successCount || 0) > 0"
                    class="btn btn-sm btn-secondary"
                    @click="handleViewRecords(job)"
                    title="查看此批次匯入的訂單明細"
                  >
                    <i class="ri-list-check-2"></i>
                    明細
                  </button>
                  <button
                    v-if="job.originalFileKey"
                    class="btn btn-sm btn-secondary"
                    :disabled="downloadingId === job.id"
                    @click="handleDownload(job)"
                    title="下載原始匯入檔案"
                  >
                    <i class="ri-download-line"></i>
                    {{ downloadingId === job.id ? '取得中...' : '下載' }}
                  </button>
                  <button
                    v-if="canRevokeJob(job)"
                    class="btn btn-sm btn-outline-warning"
                    :disabled="revokingId === job.id"
                    @click="handleRevoke(job)"
                    title="取消此批次建立的訂單並收回已發點數"
                  >
                    <i class="ri-arrow-go-back-line"></i>
                    {{ revokingId === job.id ? '撤銷中...' : '撤銷匯入' }}
                  </button>
                  <button
                    v-if="job.failedCount > 0"
                    class="btn btn-sm btn-outline-danger"
                    @click="handleViewErrors(job)"
                    title="查看錯誤詳情"
                  >
                    <i class="ri-error-warning-line"></i>
                    錯誤
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination && pagination.totalPages > 1" class="pagination">
        <button
          class="btn btn-sm btn-secondary"
          :disabled="currentPage <= 1"
          @click="changePage(currentPage - 1)"
        >
          <i class="ri-arrow-left-s-line"></i>
        </button>
        <span class="page-info">
          第 {{ currentPage }} / {{ pagination.totalPages }} 頁，共 {{ pagination.totalItems }} 筆
        </span>
        <button
          class="btn btn-sm btn-secondary"
          :disabled="currentPage >= pagination.totalPages"
          @click="changePage(currentPage + 1)"
        >
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

    <!-- 匯入明細 Modal -->
    <div v-if="showRecordsModal" class="modal-overlay" @click.self="showRecordsModal = false">
      <div class="modal-content modal-content-wide">
        <div class="modal-header">
          <h3>匯入明細 — {{ selectedJob?.jobNumber }}</h3>
          <button class="btn-close" @click="showRecordsModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="recordsLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>
          <div v-else-if="jobRecords.length === 0" class="empty-state">
            <p>此批次沒有訂單明細紀錄。</p>
            <p class="empty-hint">舊版匯入作業可能未記錄明細，僅能從訂單列表依來源篩選。</p>
          </div>
          <template v-else>
            <div class="records-table-wrapper">
              <table class="records-table">
                <thead>
                  <tr>
                    <th>列號</th>
                    <th>訂單編號</th>
                    <th>訂單日期</th>
                    <th>會員</th>
                    <th>金額</th>
                    <th>發點</th>
                    <th>訂單狀態</th>
                    <th>撤銷狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in jobRecords" :key="record.id">
                    <td>{{ record.rowNumber ?? '-' }}</td>
                    <td class="order-number">{{ record.orderNumber || '-' }}</td>
                    <td>{{ formatDate(record.orderDate) }}</td>
                    <td>{{ formatRecordMember(record) }}</td>
                    <td>{{ formatCurrency(record.totalAmount) }}</td>
                    <td>{{ formatNumber(record.pointsAwarded || 0) }}</td>
                    <td>
                      <span class="status-badge status-order" :class="`status-order-${resolveOrderStatus(record)}`">
                        {{ orderStatusLabel(record) }}
                      </span>
                    </td>
                    <td>
                      <span class="status-badge" :class="`status-revoke-${record.revokeStatus || 'active'}`">
                        {{ revokeStatusLabel(record.revokeStatus) }}
                      </span>
                    </td>
                    <td>
                      <router-link
                        v-if="record.orderId"
                        :to="`/client/orders/${record.orderId}`"
                        class="btn btn-sm btn-secondary"
                        title="查看訂單詳情"
                      >
                        <i class="ri-eye-line"></i>
                      </router-link>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="recordsPagination && recordsPagination.totalPages > 1" class="modal-pagination">
              <button
                class="btn btn-sm btn-secondary"
                :disabled="recordsPage <= 1"
                @click="changeRecordsPage(recordsPage - 1)"
              >
                <i class="ri-arrow-left-s-line"></i>
              </button>
              <span class="page-info">
                第 {{ recordsPage }} / {{ recordsPagination.totalPages }} 頁，共 {{ recordsPagination.totalItems }} 筆
              </span>
              <button
                class="btn btn-sm btn-secondary"
                :disabled="recordsPage >= recordsPagination.totalPages"
                @click="changeRecordsPage(recordsPage + 1)"
              >
                <i class="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 錯誤詳情 Modal -->
    <div v-if="showErrorModal" class="modal-overlay" @click.self="showErrorModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>錯誤詳情 — {{ selectedJob?.jobNumber }}</h3>
          <button class="btn-close" @click="showErrorModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="errorsLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>
          <div v-else-if="jobErrors.length === 0" class="empty-state">
            <p>沒有錯誤紀錄</p>
          </div>
          <div v-else class="error-list">
            <div v-for="(err, idx) in jobErrors" :key="idx" class="error-item">
              <div class="error-row">第 {{ err.rowNumber }} 行</div>
              <div class="error-type">{{ errorTypeLabel(err.errorType) }}</div>
              <div class="error-message">{{ err.errorMessage }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { orderImportService } from '@/api/modules/orderImport.js';
import { showError, showSuccess } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'ClientOrderImportHistory',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('匯入紀錄');

    const loading = ref(false);
    const jobs = ref([]);
    const pagination = ref(null);
    const currentPage = ref(1);
    const filterStatus = ref('');
    const downloadingId = ref(null);
    const revokingId = ref(null);
    const showErrorModal = ref(false);
    const errorsLoading = ref(false);
    const jobErrors = ref([]);
    const selectedJob = ref(null);
    const showRecordsModal = ref(false);
    const recordsLoading = ref(false);
    const jobRecords = ref([]);
    const recordsPagination = ref(null);
    const recordsPage = ref(1);

    const loadJobs = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          pageSize: 20
        };
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }
        const response = await orderImportService.listJobs(params);
        if (response && response.success && response.data) {
          jobs.value = response.data.items || [];
          pagination.value = response.data.pagination || null;
        }
      } catch (err) {
        console.error('載入匯入紀錄失敗:', err);
        await showError('載入失敗', err.message || '無法取得匯入紀錄');
      } finally {
        loading.value = false;
      }
    };

    const changePage = async (page) => {
      currentPage.value = page;
      await loadJobs();
    };

    const handleDownload = async (job) => {
      downloadingId.value = job.id;
      try {
        const response = await orderImportService.getDownloadUrl(job.id);
        if (response && response.success && response.data?.downloadUrl) {
          const link = document.createElement('a');
          link.href = response.data.downloadUrl;
          link.download = job.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          await showError('下載失敗', '無法取得下載連結');
        }
      } catch (err) {
        console.error('下載失敗:', err);
        await showError('下載失敗', err.message || '取得下載連結失敗');
      } finally {
        downloadingId.value = null;
      }
    };

    const handleViewErrors = async (job) => {
      selectedJob.value = job;
      showErrorModal.value = true;
      errorsLoading.value = true;
      jobErrors.value = [];
      try {
        const response = await orderImportService.getJobResult(job.id);
        if (response && response.success && response.data) {
          jobErrors.value = response.data.errors || [];
        }
      } catch (err) {
        console.error('載入錯誤詳情失敗:', err);
      } finally {
        errorsLoading.value = false;
      }
    };

    const loadJobRecords = async () => {
      if (!selectedJob.value?.id) return;
      recordsLoading.value = true;
      try {
        const response = await orderImportService.listJobRecords(selectedJob.value.id, {
          page: recordsPage.value,
          pageSize: 20
        });
        if (response && response.success && response.data) {
          jobRecords.value = response.data.items || [];
          recordsPagination.value = response.data.pagination || null;
        } else {
          jobRecords.value = [];
          recordsPagination.value = null;
        }
      } catch (err) {
        console.error('載入匯入明細失敗:', err);
        await showError('載入失敗', err.message || '無法取得匯入明細');
        jobRecords.value = [];
        recordsPagination.value = null;
      } finally {
        recordsLoading.value = false;
      }
    };

    const handleViewRecords = async (job) => {
      selectedJob.value = job;
      recordsPage.value = 1;
      showRecordsModal.value = true;
      await loadJobRecords();
    };

    const changeRecordsPage = async (page) => {
      recordsPage.value = page;
      await loadJobRecords();
    };

    const handleNewImport = () => {
      router.push('/client/orders/import');
    };

    const canRevokeJob = (job) => {
      if (!job || job.revokedAt) return false;
      if (!['completed', 'partial'].includes(job.status)) return false;
      if ((job.successCount || 0) <= 0) return false;
      return (job.revocableCount ?? job.successCount) > 0;
    };

    const escapeHtml = (value) => String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const handleRevoke = async (job) => {
      const confirmPhrase = '確定撤銷';
      const safeFileName = escapeHtml(job.fileName || '-');
      const safeJobNumber = escapeHtml(job.jobNumber || '-');

      const result = await Swal.fire({
        icon: 'warning',
        title: '確認撤銷匯入作業',
        html: `
          <div style="text-align: left; padding: 0 8px;">
            <p style="margin: 0 0 12px 0; font-size: 14px; color: #555;">
              請先確認以下資訊是否為要撤銷的批次：
            </p>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 16px; font-size: 13px; color: #334155; line-height: 1.7;">
              <div><strong>作業編號：</strong><code>${safeJobNumber}</code></div>
              <div style="margin-top: 6px; word-break: break-all;"><strong>匯入檔案：</strong>${safeFileName}</div>
              <div style="margin-top: 6px;"><strong>成功筆數：</strong>${formatNumber(job.revocableCount ?? job.successCount)} 筆</div>
            </div>
            <p style="margin: 0 0 12px 0; font-size: 14px; color: #555;">
              將把此批次訂單標記為「已取消」，並嘗試收回已發放的消費點數。匯入作業紀錄會保留。
            </p>
            <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 10px 14px; border-radius: 4px; font-size: 13px; color: #856404; margin-bottom: 4px;">
              若會員已使用部分點數，可能無法全數收回。此操作無法一鍵復原。
            </div>
          </div>
        `,
        input: 'text',
        inputLabel: `請輸入「${confirmPhrase}」以確認撤銷`,
        inputPlaceholder: confirmPhrase,
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false'
        },
        inputValidator: (value) => {
          if ((value || '').trim() !== confirmPhrase) {
            return `請輸入「${confirmPhrase}」以確認`;
          }
        },
        showCancelButton: true,
        confirmButtonText: '確認撤銷',
        cancelButtonText: '取消',
        confirmButtonColor: '#e0a800',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        width: '520px'
      });

      if (!result.isConfirmed) return;

      revokingId.value = job.id;
      try {
        const response = await orderImportService.revokeImportJob(job.id);
        if (response && response.success) {
          const data = response.data || {};
          if (data.failedCount > 0) {
            const failureDetails = Array.isArray(data.failures) && data.failures.length
              ? data.failures
                .slice(0, 5)
                .map((f) => `${f.orderNumber || f.orderId || '未知訂單'}：${f.message || '撤銷失敗'}`)
                .join('\n')
              : '';
            await showError(
              '部分撤銷失敗',
              [
                `已撤銷 ${data.revokedCount || 0} 筆，失敗 ${data.failedCount} 筆。`,
                failureDetails,
                '請至訂單列表檢查失敗項目，或聯繫技術支援。'
              ].filter(Boolean).join('\n')
            );
          } else {
            await showSuccess(
              '撤銷完成',
              `已撤銷 ${data.revokedCount || 0} 筆訂單並收回點數`
            );
          }
          await loadJobs();
        } else {
          await showError('撤銷失敗', response?.message || '撤銷匯入作業失敗');
        }
      } catch (err) {
        console.error('撤銷匯入作業失敗:', err);
        await showError('撤銷失敗', err.response?.data?.message || err.message || '撤銷匯入作業失敗');
      } finally {
        revokingId.value = null;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined || amount === '') return '-';
      const value = Number(amount);
      if (!Number.isFinite(value)) return '-';
      return `NT$ ${value.toLocaleString('zh-TW')}`;
    };

    const formatRecordMember = (record) => {
      if (record.memberName) return record.memberName;
      if (record.memberPhone) return record.memberPhone;
      if (record.memberEmail) return record.memberEmail;
      return '非會員';
    };

    const resolveOrderStatus = (record) => {
      if (record.orderStatus === 'cancelled' || record.legacyStatus === 'cancelled') return 'cancelled';
      if (record.orderStatus === 'closed' || record.legacyStatus === 'completed') return 'closed';
      return 'in_progress';
    };

    const orderStatusLabel = (record) => {
      const status = resolveOrderStatus(record);
      const map = {
        cancelled: '已取消',
        closed: '已完成',
        in_progress: '進行中'
      };
      return map[status] || '-';
    };

    const revokeStatusLabel = (status) => {
      const map = {
        active: '有效',
        revoked: '已撤銷',
        failed: '撤銷失敗'
      };
      return map[status] || status || '有效';
    };

    const formatLabel = (format) => {
      const map = { excel: 'Excel', csv: 'CSV', json: 'JSON' };
      return map[format] || format;
    };

    const statusLabel = (status) => {
      const map = {
        pending: '待處理',
        processing: '處理中',
        completed: '已完成',
        partial: '部分成功',
        failed: '失敗'
      };
      return map[status] || status;
    };

    const errorTypeLabel = (type) => {
      const map = {
        validation: '格式錯誤',
        duplicate: '重複訂單',
        customer_not_found: '找不到會員',
        system: '系統錯誤'
      };
      return map[type] || type;
    };

    onMounted(loadJobs);

    return {
      pageTitle,
      loading,
      jobs,
      pagination,
      currentPage,
      filterStatus,
      downloadingId,
      revokingId,
      showErrorModal,
      errorsLoading,
      jobErrors,
      selectedJob,
      showRecordsModal,
      recordsLoading,
      jobRecords,
      recordsPagination,
      recordsPage,
      loadJobs,
      changePage,
      handleDownload,
      handleViewErrors,
      handleViewRecords,
      changeRecordsPage,
      handleRevoke,
      canRevokeJob,
      handleNewImport,
      formatDate,
      formatNumber,
      formatCurrency,
      formatRecordMember,
      orderStatusLabel,
      resolveOrderStatus,
      revokeStatusLabel,
      formatLabel,
      statusLabel,
      errorTypeLabel
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-wrapper table {
  min-width: 1100px;
}

.job-number {
  font-family: monospace;
  font-size: 13px;
  color: #555;
}

.file-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.format-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.format-excel { background: #e8f5e9; color: #2e7d32; }
.format-csv   { background: #e3f2fd; color: #1565c0; }
.format-json  { background: #fff3e0; color: #e65100; }

.count-success { color: #2e7d32; font-weight: 600; }
.count-error   { color: #c62828; font-weight: 600; }
.count-skip    { color: #795548; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-pending    { background: #f5f5f5; color: #757575; }
.status-processing { background: #e3f2fd; color: #1565c0; }
.status-completed  { background: #e8f5e9; color: #2e7d32; }
.status-partial    { background: #fff8e1; color: #f57f17; }
.status-failed     { background: #ffebee; color: #c62828; }
.status-revoked    { background: #fff3e0; color: #e65100; margin-left: 6px; }

.action-group {
  display: flex;
  gap: 6px;
}

.btn-outline-danger {
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background: #ffebee;
}

.btn-outline-warning {
  background: transparent;
  border: 1px solid #e0a800;
  color: #b8860b;
}

.btn-outline-warning:hover {
  background: #fff8e1;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #eee;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 640px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-content-wide {
  max-width: 1080px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;
  padding: 4px;
  line-height: 1;
}

.modal-body {
  overflow-y: auto;
  padding: 16px 20px;
  flex: 1;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.error-item {
  padding: 12px;
  background: #fff8f8;
  border-left: 3px solid #dc3545;
  border-radius: 0 4px 4px 0;
}

.error-row {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.error-type {
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 2px;
}

.error-message {
  font-size: 13px;
  color: #333;
}

.empty-hint {
  font-size: 13px;
  color: #888;
  margin-top: 8px;
}

.records-table-wrapper {
  overflow-x: auto;
}

.records-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  font-size: 13px;
}

.records-table th,
.records-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
  vertical-align: middle;
}

.records-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.order-number {
  font-family: monospace;
  font-size: 12px;
}

.status-order {
  font-size: 11px;
}

.status-order-cancelled { background: #ffebee; color: #c62828; }
.status-order-closed { background: #e8f5e9; color: #2e7d32; }
.status-order-in_progress { background: #e3f2fd; color: #1565c0; }

.status-revoke-active { background: #e8f5e9; color: #2e7d32; }
.status-revoke-revoked { background: #fff3e0; color: #e65100; }
.status-revoke-failed { background: #ffebee; color: #c62828; }

.modal-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #eee;
}

.text-muted {
  color: #999;
}
</style>
